import { useEffect, useState } from "react";
import type { ChatMessage } from "./types";

const BOT_TYPING_MS = 900;
const BOT_PAUSE_MS = 700;
const USER_PAUSE_MS = 800;
const DEFAULT_LOOP_RESTART_MS = 5000;
const INITIAL_DELAY_MS = 600;

export function useTypewriter(
  messages: ChatMessage[],
  loop = true,
  loopRestartMs: number = DEFAULT_LOOP_RESTART_MS,
) {
  const [step, setStep] = useState(0);
  const [typing, setTyping] = useState(false);
  const total = messages.length;

  useEffect(() => {
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const schedule = (fn: () => void, ms: number) => {
      timers.push(setTimeout(fn, ms));
    };

    const advance = (i: number) => {
      if (cancelled) return;
      if (i >= total) {
        if (loop) {
          schedule(() => {
            setStep(0);
            advance(0);
          }, loopRestartMs);
        }
        return;
      }
      const m = messages[i];
      if (!m) return;

      if (m.from === "bot") {
        setTyping(true);
        schedule(() => {
          setTyping(false);
          setStep(i + 1);
          schedule(() => advance(i + 1), BOT_PAUSE_MS);
        }, BOT_TYPING_MS);
      } else {
        setStep(i + 1);
        schedule(() => advance(i + 1), USER_PAUSE_MS);
      }
    };

    schedule(() => advance(0), INITIAL_DELAY_MS);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [total, loop, loopRestartMs, messages]);

  return { step, typing };
}
